import { getVisitId } from '../analytics/visitId';
import { CUSTOM_COMPONENTS, DeviceComponent } from './deviceComponents';
import { deletePath, getPath } from './objectPath';

export type DeviceStatus = 'ok' | 'partial' | 'timeout' | 'error' | 'unavailable';

export interface DeviceError {
  type: string;
  component?: string;
}

export interface DeviceCollection {
  visitId: string;
  status: DeviceStatus;
  elapsedMs: number;
  libraryVersion?: string;
  stableKeys?: string[];
  components?: Record<string, unknown>;
  errors?: DeviceError[];
}

interface LibraryResponse {
  components: Record<string, unknown>;
  version: string;
  error?: Array<{ type: string; component?: string }>;
}

interface ThumbmarkInstance {
  includeComponent(name: string, fn: DeviceComponent): void;
  get(): Promise<LibraryResponse>;
}

export interface ThumbmarkModule {
  Thumbmark: new (options: typeof THUMBMARK_OPTIONS) => ThumbmarkInstance;
}

export type LibraryLoader = () => Promise<ThumbmarkModule>;

export const COLLECT_TIMEOUT_MS = 1500;
export const MAX_COMPONENTS_BYTES = 32 * 1024;

// `logging` defaults to true upstream: a sampled share of visits would ship components to the
// vendor's servers and load a remote script. Client data does not leave our edge.
export const THUMBMARK_OPTIONS = {
  logging: false,
  timeout: 1200,
  stabilize: ['private', 'iframe'],
  exclude: ['permissions'],
};

// Paths meant for matching across visits. Everything else in `components` is diagnostics: it
// drifts with browser updates, settings or free disk space.
export const STABLE_KEYS = [
  'uach.platform',
  'uach.mobile',
  'uach.model',
  'uach.platformVersion',
  'uach.architecture',
  'uach.bitness',
  'display.long',
  'display.short',
  'display.dpr',
  'display.colorDepth',
  'display.gamut',
  'display.hdr',
  'display.pointer',
  'display.hover',
  'cpu.hardwareConcurrency',
  'cpu.deviceMemory',
  'cpu.maxTouchPoints',
  'glparams',
  'gpu',
  'intl.timeZone',
  'intl.calendar',
  'intl.numberingSystem',
  'intl.hourCycle',
  'intl.languages',
  'hardware.videocard',
  'hardware.architecture',
  'hardware.jsHeapSizeLimit',
  'fonts',
  'speech',
  'webrtc',
  'canvas',
  'audio',
  'math',
  'locales',
];

// Heaviest volatile parts first; stable identity is the last thing to give up.
const DROP_FOR_SIZE = ['system.useragent', 'uach.fullVersionList', 'fonts', 'speech'];

const byteLength = (value: unknown): number => new Blob([JSON.stringify(value)]).size;

const capSize = (components: Record<string, unknown>) => {
  const kept = JSON.parse(JSON.stringify(components)) as Record<string, unknown>;
  const dropped: string[] = [];
  for (const path of DROP_FOR_SIZE) {
    if (byteLength(kept) <= MAX_COMPONENTS_BYTES) break;
    if (deletePath(kept, path)) dropped.push(path);
  }
  if (byteLength(kept) > MAX_COMPONENTS_BYTES) {
    return { components: undefined, dropped: [...dropped, '*'] };
  }
  return { components: kept, dropped };
};

const loadThumbmark: LibraryLoader = () =>
  import('@thumbmarkjs/thumbmarkjs') as unknown as Promise<ThumbmarkModule>;

export const createDeviceCollector = (
  load: LibraryLoader,
  timeoutMs: number = COLLECT_TIMEOUT_MS,
  components: Record<string, DeviceComponent> = CUSTOM_COMPONENTS,
  // The visit id is shared with everything else that reports about this page load, so it is taken
  // from one owner rather than generated here. Still a parameter: the tests build their own
  // collectors and must be able to pin the id.
  visitId: string = getVisitId(),
): (() => Promise<DeviceCollection>) => {
  let pending: Promise<DeviceCollection> | null = null;

  const run = async (): Promise<DeviceCollection> => {
    const startedAt = Date.now();
    const elapsedMs = () => Date.now() - startedAt;

    const attempt = async (): Promise<DeviceCollection> => {
      let library: ThumbmarkModule;
      try {
        library = await load();
      } catch {
        return { visitId, status: 'unavailable', elapsedMs: elapsedMs() };
      }
      try {
        const instance = new library.Thumbmark(THUMBMARK_OPTIONS);
        Object.entries(components).forEach(([name, fn]) => instance.includeComponent(name, fn));
        const result = await instance.get();
        const libraryErrors = result.error ?? [];
        if (libraryErrors.some((e) => e.type === 'fatal')) {
          return {
            visitId,
            status: 'error',
            elapsedMs: elapsedMs(),
            libraryVersion: result.version,
          };
        }
        const capped = capSize(result.components ?? {});
        const errors: DeviceError[] = [
          ...libraryErrors.map(({ type, component }) => (component ? { type, component } : { type })),
          ...capped.dropped.map((component) => ({ type: 'dropped_for_size', component })),
        ];
        const kept = capped.components;
        return {
          visitId,
          status: errors.length ? 'partial' : 'ok',
          elapsedMs: elapsedMs(),
          libraryVersion: result.version,
          ...(kept
            ? {
                components: kept,
                stableKeys: STABLE_KEYS.filter((key) => getPath(kept, key) !== undefined),
              }
            : {}),
          ...(errors.length ? { errors } : {}),
        };
      } catch {
        return { visitId, status: 'error', elapsedMs: elapsedMs() };
      }
    };

    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<DeviceCollection>((resolve) => {
      timer = setTimeout(
        () => resolve({ visitId, status: 'timeout', elapsedMs: elapsedMs() }),
        timeoutMs,
      );
    });
    try {
      return await Promise.race([attempt(), timeout]);
    } finally {
      clearTimeout(timer);
    }
  };

  return () => {
    if (!pending) pending = run();
    return pending;
  };
};

export const collectDevice = createDeviceCollector(loadThumbmark);

export const componentCount = (collection: DeviceCollection): number =>
  collection.components ? Object.keys(collection.components).length : 0;
